import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useColumnOrderSave } from "../../../core/common/useColumnOrderSave";
import GridView from "../../../core/common/GridView";
import CollapseHeader from "../../../core/common/collapse-header";
import Table from "../../../core/common/dataTable";
import {
  fetchAllProductTypes,
  createProductType,
  updateProductType,
  deleteProductType,
  ProductType,
  exportProductTypeExcel,
  exportProductTypePDF,
  exportSelectedProductTypeExcel,
  exportSelectedProductTypePDF,
  deleteProductTypeBulk,
  importProductTypeExcel,
  ProductTypeImportResult,
} from "../../../api/producttype";
import { toast } from "react-toastify";
import { highlightMatch } from "../../../core/common/highlightmatch";
import PageLoadProgressBar from "../../../core/data/json/pageLoadProgressBar";
import DateRangePicker from "react-bootstrap-daterangepicker";
import { initialSettings } from "../../../core/common/selectoption/selectoption";
import { Dropdown, Menu, Button, Checkbox, Pagination } from "antd";
import { DownOutlined, FunnelPlotFilled } from "@ant-design/icons";
import {
  validateProducttype,
  validateLeadStatus,
  ValidationResult,
  validateNoDoubleSpacesLead,
  validateNotOnlySymbolsLead,
  validateNoRepeatedCharactersLead,
  validateNoEdgeSymbols,
  validateNotOnlyNumbersLead,
  validateProductTypeDuplicate,
} from "../../../api/leadValidation";
import AnimatedBadge from "../../../core/common/AnimatedBadge/index";
import CountUp from "react-countup";
import KpiCard from "../../../core/common/KPIcard";
import { RootState } from "../../../core/data/redux/store";
import { useSelector } from "react-redux";
import { all_routes } from "../../router/all_routes";
import Tip from "../../../core/common/tooltip";
import ColumnManagement from "../../../core/common/ColumnManagement";
import SubmoduleHoverCard from "../../../core/common/submoduleHoverCard";
import ScrollToTop from "../../../core/common/ScrollToTop";
import ModalDeleteEffect from "../../../core/common/ModalDeleteEffect";

const Producttype = () => {
  const [dataSource, setDataSource] = useState<ProductType[]>([]);
  const [orderedData, setOrderedData] = useState<ProductType[]>([]);
  const [viewMode, setViewMode] = useState<"list" | "grid">("list");
  const [editProduct, setEditProduct] = useState<ProductType | null>(null);
  const [deleteProductId, setDeleteProductId] = useState<string | null>(null);
  const [newName, setNewName] = useState("");
  const [newStatus, setNewStatus] = useState("Active");
  const [editName, setEditName] = useState("");
  const [editStatus, setEditStatus] = useState("Active");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const userRole = useSelector((state: RootState) => state.auth.role);
  const route = all_routes;
  const [selectedDateRange, setSelectedDateRange] = useState<
    [Date, Date] | null
  >(null);
  const [activeRangeLabel, setActiveRangeLabel] = useState("Last 30 Days");
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [deleting, setDeleting] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] =
    useState<ProductTypeImportResult | null>(null);
  const [sortOption, setSortOption] = useState("recently_added");
  const [saving, setSaving] = useState(false);
  // Column visibility
  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>(
    {
      index: true,
      product_name: true,
      created_at: true,
      status: true,
      action: true,
    }
  );
  const defaultColumnOrder = [
    "index",
    "product_name",
    "created_at",
    "status",
    "action",
  ];

  // Column order state for drag and drop (only non-sticky columns)
  const [columnKeys, setColumnKeys] = useState<string[]>(() => {
    const saved = localStorage.getItem("producttypeColumnKeys");
    if (saved) return JSON.parse(saved);
    return defaultColumnOrder;
  });

  // Hook for save prompt on navigation/refresh
  const { SaveModal } = useColumnOrderSave({
    columnKeys,
    localStorageKey: "producttypeColumnKeys",
    defaultOrder: defaultColumnOrder,
  });
  const [statusFilter, setStatusFilter] = useState("all");
  const [openAccordion, setOpenAccordion] = useState<string | null>(null);
  const [filterNames, setFilterNames] = useState<string[]>([]);
  const [filterCreatedAts, setFilterCreatedAts] = useState<string[]>([]);
  const [filterNamesSearch, setFilterNamesSearch] = useState("");
  const [filterCreatedAtsSearch, setFilterCreatedAtsSearch] = useState("");
  const [filterStatuses, setFilterStatuses] = useState<string[]>([]);
  const [filterStatusesSearch, setFilterStatusesSearch] = useState("");
  const [alphaFilter, setAlphaFilter] = useState<string>("All");

  const [addNameError, setAddNameError] = useState("");
  const [addStatusError, setAddStatusError] = useState("");

  const [editNameError, setEditNameError] = useState("");
  const [editStatusError, setEditStatusError] = useState("");
  const [isCreateValid, setIsCreateValid] = useState(false);
  const [isEditValid, setIsEditValid] = useState(false);
  useEffect(() => {
    const styleId = "animated-badge-keyframes";
    if (!document.getElementById(styleId)) {
      const styleSheet = document.createElement("style");
      styleSheet.id = styleId;
      styleSheet.innerText = `
        @keyframes shine {
          0% {
            transform: skewX(-20deg) translateX(-150%);
          }
          50% {
             transform: skewX(-20deg) translateX(150%);
          }
          100% {
            transform: skewX(-20deg) translateX(150%);
          }
        }
      `;
      document.head.appendChild(styleSheet);
    }
  }, []);

  useEffect(() => {
    const nameValid = validateProducttype(newName).valid;
    const statusValid = validateLeadStatus(newStatus).valid;

    setIsCreateValid(nameValid && statusValid);
  }, [newName, newStatus]);
  useEffect(() => {
    const nameValid = validateProducttype(editName).valid;
    const statusValid = validateLeadStatus(editStatus).valid;

    setIsEditValid(nameValid && statusValid);
  }, [editName, editStatus]);

  const handleAccordionToggle = (key: string) => {
    setOpenAccordion((prev) => (prev === key ? null : key));
  };

  // 🔹 Toggle checkbox filter item
  const handleFilterChange = (
    value: string,
    current: string[],
    setter: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    if (current.includes(value)) {
      setter(current.filter((v) => v !== value));
    } else {
      setter([...current, value]);
    }
  };

  // 🔹 Unique Product Names
  const uniqueNames = useMemo(
    () =>
      Array.from(new Set(dataSource.map((d) => d.product_name || ""))).filter(
        Boolean
      ),
    [dataSource]
  );

  const uniqueStatuses = useMemo(
    () =>
      Array.from(new Set(dataSource.map((d) => d.status?.trim() || ""))).filter(
        (s) => s
      ),
    [dataSource]
  );

  // 🔹 Unique Created Dates
  const uniqueCreatedAts = useMemo(() => {
    const formatted = dataSource
      .map((d) => {
        if (!d.created_at) return "";
        const dt = new Date(d.created_at);
        if (isNaN(dt.getTime())) return "";
        return dt.toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      })
      .filter((x) => x);
    return Array.from(new Set(formatted));
  }, [dataSource]);

  const handleColumnToggle = (columnKey: string) => {
    setVisibleColumns((prev) => ({ ...prev, [columnKey]: !prev[columnKey] }));
  };

  const loadProducts = async () => {
    setLoading(true); // Step 2: Start loading

    const data = await fetchAllProductTypes();
    setDataSource(data);
    setOrderedData(data);

    setLoading(false); // Step 3: End loading
  };

  useEffect(() => {
    loadProducts();
  }, []);

  // Make modals draggable
  useEffect(() => {
    const makeModalDraggable = (modalId: string) => {
      const modalElement = document.getElementById(modalId);
      if (!modalElement) return;

      const modalDialog = modalElement.querySelector(
        ".modal-dialog"
      ) as HTMLElement;
      const modalHeader = modalElement.querySelector(
        ".modal-header"
      ) as HTMLElement;

      if (!modalDialog || !modalHeader) return;

      let isDragging = false;
      let currentX = 0;
      let currentY = 0;
      let initialX = 0;
      let initialY = 0;
      let startX = 0;
      let startY = 0;

      // Reset position when modal is shown
      const resetPosition = () => {
        // Reset all position variables
        currentX = 0;
        currentY = 0;
        initialX = 0;
        initialY = 0;
        startX = 0;
        startY = 0;
        isDragging = false;

        // Remove only the inline styles that were added during dragging
        modalDialog.style.position = "";
        modalDialog.style.top = "";
        modalDialog.style.left = "";
        modalDialog.style.transform = "";
        modalDialog.style.margin = "";
        modalDialog.style.transition = "";
        modalDialog.style.width = "";
        modalDialog.style.maxWidth = "";

        // Restore centered class if it was removed
        if (!modalDialog.classList.contains("modal-dialog-centered")) {
          modalDialog.classList.add("modal-dialog-centered");
        }
      };

      // Listen for modal show event - reset when modal is about to show
      const handleShow = () => {
        // Reset immediately when modal starts showing
        resetPosition();
      };

      // Listen for modal shown event - ensure reset after modal is fully displayed
      const handleShown = () => {
        // Double-check reset after modal is fully shown
        resetPosition();
      };

      // Listen for modal hidden event - reset when modal is fully hidden
      const handleHidden = () => {
        resetPosition();
      };

      modalElement.addEventListener("show.bs.modal", handleShow);
      modalElement.addEventListener("shown.bs.modal", handleShown);
      modalElement.addEventListener("hidden.bs.modal", handleHidden);

      const dragStart = (e: MouseEvent) => {
        // Only allow dragging from header, not from close button or other interactive elements
        const target = e.target as HTMLElement;
        if (
          target.closest(".btn-close") ||
          target.closest("button") ||
          target.closest("input") ||
          target.closest("select")
        ) {
          return;
        }

        if (
          e.target === modalHeader ||
          modalHeader.contains(e.target as Node)
        ) {
          isDragging = true;
          modalHeader.style.cursor = "move";

          // Get current position and size of modal dialog
          const rect = modalDialog.getBoundingClientRect();
          startX = rect.left;
          startY = rect.top;

          // Get computed styles to preserve the modal's size
          const computedStyle = getComputedStyle(modalDialog);
          const currentWidth = computedStyle.width;
          const currentMaxWidth = computedStyle.maxWidth;
          const currentMinWidth = computedStyle.minWidth;

          // Remove centered class to allow free movement
          modalDialog.classList.remove("modal-dialog-centered");

          // Set initial position based on current position
          modalDialog.style.position = "fixed";
          modalDialog.style.top = `${startY}px`;
          modalDialog.style.left = `${startX}px`;
          modalDialog.style.margin = "0";
          modalDialog.style.transform = "none";
          modalDialog.style.transition = "none";

          // Preserve the modal's width and max-width to prevent size change
          if (
            currentWidth &&
            currentWidth !== "auto" &&
            currentWidth !== "0px"
          ) {
            modalDialog.style.width = currentWidth;
          }
          if (
            currentMaxWidth &&
            currentMaxWidth !== "none" &&
            currentMaxWidth !== "0px"
          ) {
            modalDialog.style.maxWidth = currentMaxWidth;
          }
          if (
            currentMinWidth &&
            currentMinWidth !== "0px" &&
            currentMinWidth !== "auto"
          ) {
            modalDialog.style.minWidth = currentMinWidth;
          }

          initialX = e.clientX - startX;
          initialY = e.clientY - startY;
        }
      };

      const drag = (e: MouseEvent) => {
        if (!isDragging) return;

        e.preventDefault();

        // Calculate new position
        const newX = e.clientX - initialX;
        const newY = e.clientY - initialY;

        // Get modal dialog dimensions
        const rect = modalDialog.getBoundingClientRect();
        const maxX = window.innerWidth - rect.width;
        const maxY = window.innerHeight - rect.height;

        // Constrain to viewport
        const constrainedX = Math.max(0, Math.min(newX, maxX));
        const constrainedY = Math.max(0, Math.min(newY, maxY));

        // Update position
        modalDialog.style.left = `${constrainedX}px`;
        modalDialog.style.top = `${constrainedY}px`;

        // Store current position for next drag
        currentX = constrainedX;
        currentY = constrainedY;
      };

      const dragEnd = () => {
        if (isDragging) {
          isDragging = false;
          modalHeader.style.cursor = "move";
          modalDialog.style.transition = "";
          // Keep the fixed positioning during drag, but allow reset on next open
        }
      };

      // Add cursor style to header
      modalHeader.style.cursor = "move";
      modalHeader.style.userSelect = "none";

      // Event listeners
      modalHeader.addEventListener("mousedown", dragStart);
      document.addEventListener("mousemove", drag);
      document.addEventListener("mouseup", dragEnd);

      // Cleanup function
      return () => {
        modalElement.removeEventListener("show.bs.modal", handleShow);
        modalElement.removeEventListener("shown.bs.modal", handleShown);
        modalElement.removeEventListener("hidden.bs.modal", handleHidden);
        modalHeader.removeEventListener("mousedown", dragStart);
        document.removeEventListener("mousemove", drag);
        document.removeEventListener("mouseup", dragEnd);
      };
    };

    // Make all three modals draggable
    const cleanup1 = makeModalDraggable("add_modal");
    const cleanup2 = makeModalDraggable("edit_modal");
    const cleanup3 = makeModalDraggable("delete_modal");

    return () => {
      cleanup1?.();
      cleanup2?.();
      cleanup3?.();
    };
  }, []);

  const handleCreateProduct = async () => {
    // Validation
    const nameResult = validateProducttype(newName);
    if (!nameResult.valid) {
      toast.error(nameResult.message);
      return;
    }

    const statusResult = validateLeadStatus(newStatus);
    if (!statusResult.valid) {
      toast.error(statusResult.message);
      return;
    }

    setSaving(true); // Start loading

    try {
      const newProduct: ProductType = {
        product_name: newName.trim(),
        status: newStatus,
      };

      const created = await createProductType(newProduct);

      if (created) {
        setNewName("");
        setNewStatus("Active");
        loadProducts(); // Refresh table
      }
    } catch (err) {
      // Error toast already handled in API layer (if implemented)
      // Optional: toast.error("Failed to create product type");
    } finally {
      setSaving(false); // Always stop loading
    }
  };

  const handleEditClick = (record: ProductType) => {
    setEditProduct(record);
    setEditName(record.product_name);
    setEditStatus(record.status || "Active");
  };

  const handleSaveEdit = async () => {
    if (!editProduct?.product_type_id) {
      toast.error("Invalid product type selected.");
      return;
    }

    // Validation
    const nameResult = validateProducttype(editName);
    if (!nameResult.valid) {
      toast.error(nameResult.message);
      return;
    }

    const statusResult = validateLeadStatus(editStatus);
    if (!statusResult.valid) {
      toast.error(statusResult.message);
      return;
    }

    setSaving(true); // Start loading

    try {
      const updated: ProductType = {
        product_name: editName.trim(),
        status: editStatus,
      };

      const success = await updateProductType(
        editProduct.product_type_id,
        updated
      );

      if (success) {
        loadProducts(); // Refresh table
        // Close modal (simulates click on close button)

        // Alternative: document.getElementById("edit_modal")?.click(); // if you use data-bs-dismiss on button
      }
    } catch (err) {
      // Error toast already handled in API layer (if implemented)
      // Optional: toast.error("Failed to update product type");
    } finally {
      setSaving(false); // Always stop loading
    }
  };
  const handleConfirmDelete = async () => {
    if (!deleteProductId) return;

    setSaving(true); // Start loading

    try {
      const success = await deleteProductType(deleteProductId);

      if (success) {
        loadProducts(); // Refresh table
        // Optional: close modal automatically
      }
    } catch (err) {
      // Error toast already handled in API layer (if implemented)
      // Optional: toast.error("Failed to delete product type");
    } finally {
      setSaving(false); // Always stop loading
    }
  };
  const activeCount = useMemo(() => {
    return dataSource.filter((item) => item.status?.toLowerCase() === "active")
      .length;
  }, [dataSource]);

  const inactiveCount = useMemo(() => {
    return dataSource.filter(
      (item) => item.status?.toLowerCase() === "inactive"
    ).length;
  }, [dataSource]);

  const totalCount = useMemo(() => {
    return dataSource.length;
  }, [dataSource]);

  const thisMonthCount = useMemo(() => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    return dataSource.filter((item) => {
      if (!item.created_at) return false;

      const createdDate = new Date(item.created_at);
      return (
        createdDate.getMonth() === currentMonth &&
        createdDate.getFullYear() === currentYear
      );
    }).length;
  }, [dataSource]);

  const filteredData = useMemo(() => {
    const q = searchTerm.toLowerCase();

    let filtered = dataSource.filter((item) => {
      const name = item.product_name?.toLowerCase() || "";
      const createdStr = item.created_at || "";
      const itemStatus = item.status?.toLowerCase() || "";

      const matchName =
        filterNames.length === 0 ||
        filterNames.includes(item.product_name || "");

      const createdFmt = item.created_at
        ? new Date(item.created_at).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
        : "";

      const alphaMatch =
        alphaFilter === "All" ||
        (item.product_name &&
          item.product_name
            .toLowerCase()
            .startsWith(alphaFilter.toLowerCase()));

      const matchCreated =
        filterCreatedAts.length === 0 || filterCreatedAts.includes(createdFmt);

      const matchStatus =
        filterStatuses.length === 0 ||
        filterStatuses.includes(item.status || "");

      const matchesSearch =
        name.includes(q) ||
        itemStatus.includes(q) ||
        createdFmt.toLowerCase().includes(q);


      // Date range match
      let matchesDate = true;
      if (selectedDateRange && createdStr) {
        const createdDate = new Date(createdStr);
        const [start, end] = selectedDateRange;
        matchesDate = createdDate >= start && createdDate <= end;
      }

      if (statusFilter !== "all" && itemStatus !== statusFilter.toLowerCase()) {
        return false;
      }

      return (
        matchesSearch &&
        matchesDate &&
        matchName &&
        matchCreated &&
        matchStatus &&
        alphaMatch
      );
    });

    // Sorting
    if (sortOption === "ascending") {
      filtered.sort((a, b) =>
        (a.product_name || "").localeCompare(b.product_name || "")
      );
    } else if (sortOption === "descending") {
      filtered.sort((a, b) =>
        (b.product_name || "").localeCompare(a.product_name || "")
      );
    } else if (sortOption === "recently_added") {
      filtered.sort(
        (a, b) =>
          new Date(b.created_at || "").getTime() -
          new Date(a.created_at || "").getTime()
      );
    }

    return filtered;
  }, [
    searchTerm,
    dataSource,
    sortOption,
    selectedDateRange,
    statusFilter,
    filterNames,
    filterCreatedAts,
    filterStatuses,
    alphaFilter,
  ]);

  const columns = useMemo(
    () => [
      {
        title: "S.No",
        key: "index",
        width: 80,
        align: "center" as const,
        render: (_: any, __: any, index: number) => (
          <span className="text-muted fw-medium">
            {(currentPage - 1) * pageSize + index + 1}
          </span>
        ),
      },

      {
        title: (
          <div className="d-flex align-items-center justify-content-between">
            <span className="fw-semibold text-dark">Product Type Name</span>

            {/* Alphabetical Filter */}
            <Dropdown
              trigger={["click"]}
              onOpenChange={(open) =>
                open && setTimeout(() => document.body.click(), 0)
              }
              overlay={
                <Menu
                  selectedKeys={alphaFilter ? [alphaFilter] : ["All"]}
                  onClick={({ key }) =>
                    setAlphaFilter(key === "All" ? "" : key)
                  }
                  style={{ width: 50, maxHeight: 250, overflow: "auto" }}
                >
                  <Menu.Item key="All" className="text-center">
                    All
                  </Menu.Item>
                  {Array.from("ABCDEFGHIJKLMNOPQRSTUVWXYZ").map((letter) => (
                    <Menu.Item key={letter} className="text-center">
                      {letter}
                    </Menu.Item>
                  ))}
                </Menu>
              }
            >
              <Button
                size="small"
                type="text"
                className="border rounded ms-2 px-2 py-1 bg-light hover-bg-white"
                onClick={(e) => e.stopPropagation()}
                title="Filter by first letter"
              >
                <span className="fw-medium text-primary">
                  {alphaFilter && alphaFilter !== "All" ? alphaFilter : "ALL"}
                </span>
                <DownOutlined
                  className="ms-1 text-muted"
                  style={{ fontSize: 10 }}
                />
              </Button>
            </Dropdown>
          </div>
        ),
        dataIndex: "product_name",
        key: "product_name",
        sorter: true,
        render: (_: any, record: ProductType) => (
          <SubmoduleHoverCard
            name={highlightMatch(record.product_name, searchTerm)}
            role="Product Type"
            producttype_name={record.product_name}
            createdAt={record.created_at}
            variant="productType"
          />
        ),
      },

      {
        title: "Created At",
        dataIndex: "created_at",
        key: "created_at",
        width: 130,
        align: "center" as const,
        sorter: true,
        render: (value: string) => {
          if (!value) return <span className="text-muted">—</span>;

          const date = new Date(value);
          if (isNaN(date.getTime()))
            return <span className="text-muted">Invalid</span>;

          const formattedDate = date.toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          });

          return <span className="text-muted">{highlightMatch(formattedDate, searchTerm)}</span>;
        },
      },

      {
        title: (
          <div className="d-flex align-items-center justify-content-between"
            onClick={(e) => e.stopPropagation()}>
            <span className="fw-semibold text-dark">Status</span>

            {/* Status Filter with Counts */}
            <Dropdown
              trigger={["click"]}
              overlay={
                <Menu
                  className="p-2 shadow-sm border-0"
                  style={{ minWidth: 220 }}
                >
                  <Menu.Item
                    key="all"
                    className="d-flex justify-content-between align-items-center py-2 rounded hover-bg-light"
                    onClick={() => setFilterStatuses([])}
                  >
                    <Checkbox checked={filterStatuses.length === 0}>
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="d-flex align-items-center">
                          All Status
                        </span>
                        <span
                          className="d-flex align-items-center justify-content-end gap-2"
                          style={{ minWidth: 48 }}
                        >
                          <span className="badge bg-light text-dark">
                            {dataSource.length}
                          </span>
                        </span>
                      </div>
                    </Checkbox>
                  </Menu.Item>

                  <Menu.Divider className="my-2" />

                  <Menu.Item
                    key="Active"
                    onClick={() => {
                      setFilterStatuses((prev) =>
                        prev.includes("Active")
                          ? prev.filter((v) => v !== "Active")
                          : [...prev, "Active"]
                      );
                    }}
                  >
                    <Checkbox checked={filterStatuses.includes("Active")}>
                      <div className="d-flex justify-content-between align-items-center w-100">
                        <span className="d-flex align-items-center">
                          Active
                        </span>
                        <span
                          className="d-flex align-items-center justify-content-end gap-2"
                          style={{ minWidth: 68 }}
                        >
                          <span className="badge bg-light text-dark">
                            {
                              dataSource.filter((d) => d.status === "Active")
                                .length
                            }
                          </span>
                        </span>
                      </div>
                    </Checkbox>
                  </Menu.Item>

                  <Menu.Item
                    key="Inactive"
                    onClick={() => {
                      setFilterStatuses((prev) =>
                        prev.includes("Inactive")
                          ? prev.filter((v) => v !== "Inactive")
                          : [...prev, "Inactive"]
                      );
                    }}
                  >
                    <Checkbox checked={filterStatuses.includes("Inactive")}>
                      <div className="d-flex justify-content-between align-items-center w-100">
                        <span className="d-flex align-items-center">
                          Inactive
                        </span>
                        <span
                          className="d-flex align-items-center justify-content-end gap-2"
                          style={{ minWidth: 58 }}
                        >
                          <span className="badge bg-light text-dark">
                            {
                              dataSource.filter((d) => d.status === "Inactive")
                                .length
                            }
                          </span>
                        </span>
                      </div>
                    </Checkbox>
                  </Menu.Item>
                </Menu>
              }
            >
              <Button
                size="small"
                type="text"
                className="ms-2"
                style={{ padding: "0 6px" }}
                onClick={(e) => e.stopPropagation()}
                title="Filter by status"
              >
                <FunnelPlotFilled
                  className={
                    filterStatuses.length > 0 ? "text-primary" : "text-muted"
                  }
                  style={{ fontSize: 16 }}
                />
                {filterStatuses.length > 0 && (
                  <span className="badge bg-primary rounded-pill ms-1 fw-bold">
                    {filterStatuses.length}
                  </span>
                )}
              </Button>
            </Dropdown>
          </div>
        ),
        dataIndex: "status",
        key: "status",
        width: 130,
        align: "center" as const,
        sorter: true,
        render: (status: string) => {
          const value = status?.toLowerCase();
          const isActive = value === "active";

          const badgeClass = isActive
            ? "bg-success"
            : value === "inactive"
              ? "bg-danger"
              : "bg-secondary";

          const iconClass = isActive
            ? "ti ti-check"
            : value === "inactive"
              ? "ti ti-x"
              : undefined;

          return (
            <AnimatedBadge
              status={status || "N/A"}
              className={`badge-status ${badgeClass}`}
              icon={iconClass}
            />
          );
        },
      },

      {
        title: <span className="fw-semibold text-dark">Actions</span>,
        key: "action",
        fixed: "right" as const,
        width: 90,
        align: "center" as const,
        render: (_: any, record: ProductType) => {
          const menu = (
            <Menu className="shadow-lg border-0 p-1">
              <Menu.Item
                key="edit"
                className="d-flex align-items-center gap-2 py-2 px-3 rounded hover-bg-light"
                data-bs-toggle="modal"
                data-bs-target="#edit_modal"
                onClick={() => handleEditClick(record)}
              >
                <i className="ti ti-edit text-primary fs-15 me-2" />
                <span className="fw-medium">Edit</span>
              </Menu.Item>

              <Menu.Item
                key="delete"
                className="d-flex align-items-center gap-2 py-2 px-3 rounded text-danger hover-bg-danger-light"
                data-bs-toggle="modal"
                data-bs-target="#delete_modal"
                onClick={() => setDeleteProductId(record.product_type_id!)}
              >
                <i className="ti ti-trash fs-15 me-2" />
                <span className="fw-medium">Delete</span>
              </Menu.Item>
            </Menu>
          );

          return (
            <Dropdown
              trigger={["click"]}
              overlay={menu}
              placement="bottomRight"
              onOpenChange={(open) =>
                open && setTimeout(() => document.body.click(), 0)
              }
            >
              <Button
                size="small"
                type="text"
                className="table-action-btn"
                onClick={(e) => e.stopPropagation()}
                title="More actions"
              >
                <i className="fa fa-ellipsis-v" />
              </Button>
            </Dropdown>
          );
        },
      },
    ],
    [currentPage, pageSize, searchTerm, alphaFilter, filterStatuses, dataSource]
  );
  const handleRowOrderChange = (newOrdered: ProductType[]) => {
    setOrderedData(newOrdered);
    // OPTIONAL: persist to backend
    // await reorderLeadSources(newOrdered.map(r => r.LeadSource_id));
  };

  const prevMonthCount = useMemo(() => {
    const now = new Date();
    const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const prevMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);

    return dataSource.filter((item) => {
      if (!item.created_at) return false;
      const date = new Date(item.created_at);
      return date >= prevMonth && date <= prevMonthEnd;
    }).length;
  }, [dataSource]);

  const thisMonthPercentage = useMemo(() => {
    if (prevMonthCount === 0) return thisMonthCount > 0 ? "100" : "0";
    return (((thisMonthCount - prevMonthCount) / prevMonthCount) * 100).toFixed(
      1
    );
  }, [thisMonthCount, prevMonthCount]);

  const activePercentage = useMemo(() => {
    if (totalCount === 0) return "0";
    return ((activeCount / totalCount) * 100).toFixed(1);
  }, [activeCount, totalCount]);

  const inactivePercentage = useMemo(() => {
    if (totalCount === 0) return "0";
    return ((inactiveCount / totalCount) * 100).toFixed(1);
  }, [inactiveCount, totalCount]);

  const handleExportAllPDF = async () => {
    try {
      const blob = await exportProductTypePDF();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `product_types_all_${new Date().toISOString().split("T")[0]
        }.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      // toast handled in API
    }
  };

  const handleExportAllExcel = async () => {
    try {
      const blob = await exportProductTypeExcel();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `product_types_all_${new Date().toISOString().split("T")[0]
        }.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      // toast handled
    }
  };

  const handleExportSelectedPDF = async () => {
    if (selectedRows.length === 0) {
      toast.warning("Please select at least one product type to export.");
      return;
    }
    try {
      const blob = await exportSelectedProductTypePDF(selectedRows);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `product_types_selected_${selectedRows.length}_${new Date().toISOString().split("T")[0]
        }.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      // handled in API
    }
  };

  const handleExportSelectedExcel = async () => {
    if (selectedRows.length === 0) {
      toast.warning("Please select at least one product type to export.");
      return;
    }
    try {
      const blob = await exportSelectedProductTypeExcel(selectedRows);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `product_types_selected_${selectedRows.length}_${new Date().toISOString().split("T")[0]
        }.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      // handled in API
    }
  };

  const handleBulkDelete = async () => {
    if (selectedRows.length === 0) {
      toast.warning("Please select at least one product type to delete.");
      return;
    }
    setDeleting(true);
    try {
      await deleteProductTypeBulk(selectedRows);
      toast.success(
        `${selectedRows.length} Product Type${selectedRows.length > 1 ? "s" : ""
        } deleted successfully`
      );
      await loadProducts(); // Adjust to your refresh function name
      setSelectedRows([]);
    } catch (err) {
      // error toast already in API
    } finally {
      setDeleting(false);
    }
  };
  return (
    <>
      <PageLoadProgressBar />
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header mb-0">
            <div className="row align-items-center">
              <div className="col-sm-8">
                <h4 className="page-title">
                  <i className="bi bi-box2 text-primary me-2" />
                  Product Types{" "}
                  <span className="count-title ms-2">
                    <CountUp
                      end={dataSource.length}
                      duration={3.0}
                      separator=","
                    />
                  </span>
                </h4>
              </div>
              <div className="col-sm-4 text-sm-end">
                <div className="head-icons">
                  <CollapseHeader />
                </div>
              </div>
            </div>
          </div>
          {/* Breadcrumb */}
          <nav aria-label="breadcrumb" className="page-header-breadcrumb mb-3">
            <ol className="breadcrumb mb-0">
              <li className="breadcrumb-item">
                <Link
                  to={
                    userRole === "admin"
                      ? route.dealsDashboard
                      : route.leadsDashboard
                  }
                  style={{
                    color: "#9595b5",
                    textDecoration: "none",
                    transition: "color 0.2s ease",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.color = "#E41F07")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.color = "#9595b5")
                  }
                >
                  Dashboard
                </Link>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                Product Type
              </li>
            </ol>
          </nav>
          {/* /Breadcrumb */}
          <div className="row g-3 mb-4">
            {(() => {
              const total = totalCount;
              const activePercent =
                total > 0 ? ((activeCount / total) * 100).toFixed(1) : 0;
              const inactivePercent =
                total > 0 ? ((inactiveCount / total) * 100).toFixed(1) : 0;
              const thisMonthPercent =
                total > 0 ? ((thisMonthCount / total) * 100).toFixed(1) : 0;

              return (
              <>
                  {/* Total */}
                  <div className="col-lg-3 col-md-6">
                    <Tip
                      text={`Total Product Types: ${totalCount} (100%)`}
                      placement="top"
                    >
                      <div className="kpi-card-wrapper">
                        <KpiCard
                          title="Product"
                          title1="Types"
                          subtitle="Total"
                          value={totalCount}
                          icon="ti ti-users"
                          color="primary"
                          tooltip="Total number of product types in the system"
                        />
                      </div>
                    </Tip>
                  </div>

                  {/* This Month */}
                  <div className="col-lg-3 col-md-6">
                    <Tip
                      text={`Added This Month: ${thisMonthCount} (${thisMonthPercent}%)`}
                      placement="top"
                    >
                      <div className="kpi-card-wrapper">
                        <KpiCard
                          title="Product"
                          title1="Types"
                          subtitle="This Month"
                          value={thisMonthCount}
                          icon="ti ti-calendar"
                          color="warning"
                          tooltip="Product types created in the current month"
                          percentage={thisMonthPercentage}
                          percentageColor={
                            parseFloat(thisMonthPercentage) > 0
                              ? "success"
                              : "danger"
                          }
                          percentageIcon={
                            parseFloat(thisMonthPercentage) > 0
                              ? "ti ti-arrow-up"
                              : "ti ti-arrow-down"
                          }
                        />
                      </div>
                    </Tip>
                  </div>

                  {/* Active */}
                  <div className="col-lg-3 col-md-6">
                    <Tip
                      text={`Active: ${activeCount} (${activePercent}%)`}
                      placement="top"
                    >
                      <div className="kpi-card-wrapper">
                        <KpiCard
                          title="Product"
                          title1="Types"
                          subtitle="Active"
                          value={activeCount}
                          icon="ti-pulse"
                          color="success"
                          tooltip="Currently active and usable product types"
                          percentage={activePercentage}
                          percentageColor="success"
                          percentageIcon="ti ti-arrow-up"
                        />
                      </div>
                    </Tip>
                  </div>

                  {/* Inactive */}
                  <div className="col-lg-3 col-md-6">
                    <Tip
                      text={`Inactive: ${inactiveCount} (${inactivePercent}%)`}
                      placement="top"
                    >
                      <div className="kpi-card-wrapper">
                        <KpiCard
                          title="Product"
                          title1="Types"
                          subtitle="Inactive"
                          value={inactiveCount}
                          icon="ti-power-off"
                          color="danger"
                          tooltip="Inactive or archived product types"
                          percentage={inactivePercentage}
                          percentageColor="danger"
                          percentageIcon="ti ti-arrow-down"
                        />
                      </div>
                    </Tip>
                  </div>
                </>
              );
            })()}
          </div>

          <div className="card">
            <div className="card-header">
              <div className="row">
                <div className="col-sm-4">
                  <div className="icon-form mb-3 mb-sm-0 position-relative ms-3">
                    <span className="form-icon">
                      <i className="ti ti-search text-muted fs-16" />
                    </span>
                    <input
                      type="text"
                      className="form-control form-control-lg ps-5 rounded-3 border shadow-sm"
                      placeholder="Search Product Type..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{ height: "42px", fontSize: "14px" }}
                    />

                    {/* Clear button appears when there's text */}
                    {searchTerm && (
                      <button
                        type="button"
                        className="btn-close position-absolute end-0 top-50 translate-middle-y me-3"
                        style={{ zIndex: 10, fontSize: "12px" }}
                        onClick={() => setSearchTerm("")}
                        aria-label="Clear search"
                      />
                    )}
                  </div>
                </div>
                <div className="col-sm-8">
                  <div className="d-flex align-items-center flex-wrap row-gap-2 justify-content-sm-end">
                    <button
                      type="button"
                      className="btn btn-outline-success d-flex align-items-center gap-2 me-2"
                      data-bs-toggle="modal"
                      aria-label="Import product types from Excel"
                      data-bs-target="#import_excel_modal"
                    >
                      <i className="ti ti-file-import fs-16" />
                      <span className="d-none d-sm-inline">Import Excel</span>
                    </button>

                    <div className="dropdown me-3">
                      <button
                        type="button"
                        className={`btn btn-outline-secondary d-flex align-items-center gap-2 dropdown-toggle ${selectedRows.length > 0
                          ? "border-primary text-primary"
                          : ""
                          }`}
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                        disabled={loading}
                      >
                        <i className="ti ti-package-export fs-16" />
                        <span className="d-none d-sm-inline">Export</span>
                        {selectedRows.length > 0 && (
                          <span className="badge bg-primary rounded-pill ms-2">
                            {selectedRows.length}
                          </span>
                        )}
                      </button>

                      <ul
                        className="dropdown-menu dropdown-menu-end shadow-lg border-0 py-3 px-2"
                        style={{ minWidth: "260px" }}
                      >
                        <li>
                          <div className="px-3 mb-2">
                            <small className="text-uppercase text-muted fw-bold">
                              Export Options
                            </small>
                          </div>
                        </li>
                        <li>
                          <hr className="dropdown-divider my-1" />
                        </li>
                        <li>
                          <div className="px-3 py-1">
                            <small className="text-muted">
                              All Records ({filteredData.length})
                            </small>
                          </div>
                        </li>
                        <li>
                          <button
                            className="dropdown-item d-flex align-items-center gap-3 py-2 rounded-3"
                            onClick={handleExportAllPDF}
                          >
                            <div className="avatar avatar-sm bg-danger-light rounded">
                              <i className="ti ti-file-type-pdf text-danger fs-14" />
                            </div>
                            <div className="text-start">
                              <div className="fw-medium">Export as PDF</div>
                              <small className="text-muted">
                                Portable Document Format
                              </small>
                            </div>
                          </button>
                        </li>
                        <li>
                          <button
                            className="dropdown-item d-flex align-items-center gap-3 py-2 rounded-3"
                            onClick={handleExportAllExcel}
                          >
                            <div className="avatar avatar-sm bg-success-light rounded">
                              <i className="ti ti-file-spreadsheet text-success fs-14" />
                            </div>
                            <div className="text-start">
                              <div className="fw-medium">Export as Excel</div>
                              <small className="text-muted">
                                Spreadsheet (.xlsx)
                              </small>
                            </div>
                          </button>
                        </li>

                        {selectedRows.length > 0 && (
                          <>
                            <li>
                              <hr className="dropdown-divider my-2" />
                            </li>
                            <li>
                              <div className="px-3 py-1">
                                <small className="text-muted">
                                  Selected Only ({selectedRows.length})
                                </small>
                              </div>
                            </li>
                            <li>
                              <button
                                className="dropdown-item d-flex align-items-center gap-3 py-2 rounded-3"
                                onClick={handleExportSelectedPDF}
                              >
                                <div className="avatar avatar-sm bg-danger-light rounded">
                                  <i className="ti ti-file-type-pdf text-danger fs-14" />
                                </div>
                                <div className="text-start">
                                  <div className="fw-medium">
                                    Export Selected as PDF
                                  </div>
                                </div>
                              </button>
                            </li>
                            <li>
                              <button
                                className="dropdown-item d-flex align-items-center gap-3 py-2 rounded-3"
                                onClick={handleExportSelectedExcel}
                              >
                                <div className="avatar avatar-sm bg-success-light rounded">
                                  <i className="ti ti-file-spreadsheet text-success fs-14" />
                                </div>
                                <div className="text-start">
                                  <div className="fw-medium">
                                    Export Selected as Excel
                                  </div>
                                </div>
                              </button>
                            </li>
                          </>
                        )}

                        {selectedRows.length === 0 && (
                          <li>
                            <div className="text-center py-4 px-3">
                              <i className="ti ti-checkbox fs-32 text-muted d-block mb-2" />
                              <small className="text-muted">
                                Select rows to export specific records
                              </small>
                            </div>
                          </li>
                        )}
                      </ul>
                    </div>

                    <Link
                      to="#"
                      className="btn btn-primary me-3"
                      data-bs-toggle="modal"
                      data-bs-target="#add_modal"
                      style={{
                        position: "relative",
                        overflow: "hidden",
                      }}
                    >
                      <i
                        className="ti ti-square-rounded-plus me-2"
                        style={{ position: "relative", zIndex: 2 }}
                      />
                      <span style={{ position: "relative", zIndex: 2 }}>
                        Add Product type
                      </span>
                      <span
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          background:
                            "linear-gradient(to right, transparent 0%, rgba(255, 255, 255, 0.4) 50%, transparent 100%)",
                          transform: "skewX(-20deg) translateX(-150%)",
                          animation: "shine 3s infinite",
                        }}
                      />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
           <div className="card-body">
                <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap row-gap-2">
                  <div className="d-flex align-items-center flex-wrap row-gap-2">
                    <div className="dropdown me-2">
                  <Link
                    to="#"
                    className="dropdown-toggle dropdown-active-toggle common-sort-dropdown-toggle"
                    data-bs-toggle="dropdown"
                  >

                    <i className="ti ti-sort-ascending-2 me-2" />
                    Sort
                  </Link>
                  <div className="dropdown-menu dropdown-menu-start">
                    <ul className="list-unstyled mb-0">
                      {[
                        { label: "Ascending", value: "ascending" },
                        { label: "Descending", value: "descending" },
                        { label: "Recently Added", value: "recently_added" },
                      ].map(({ label, value }) => {
                        const isActive = sortOption === value;
                        return (
                          <li key={value}>
                            <Link
                              to="#"
                              className={`dropdown-item d-flex justify-content-between align-items-center ${isActive ? "fw-bold text-primary" : ""
                                }`}
                              onClick={() => setSortOption(value)}
                            >
                              <span>
                                <i className="ti ti-circle-chevron-right me-1" />
                                {label}
                              </span>
                              {isActive && <i className="ti ti-check" />}
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>

                {/* Date Range Picker */}
                <div className="icon-form">
                  <span className="form-icon">
                    <i className="ti ti-calendar" />
                  </span>
                  <DateRangePicker
                    initialSettings={initialSettings}
                    onCallback={(start, end, label) => {
                      setSelectedDateRange([start.toDate(), end.toDate()]);
                      if (label) setActiveRangeLabel(label);
                    }}
                  >
                    <input
                      className="form-control bookingrange"
                      type="text"
                      readOnly
                      style={{ minWidth: "200px" }}
                    />
                  </DateRangePicker>
                </div>

                <div className="dropdown ms-2 d-flex flex-wrap">
                  <Link
                    to="#"
                    className="dropdown-toggle d-flex align-items-center gap-2 fw-medium rounded border bg-white px-3 py-2"
                    data-bs-toggle="dropdown"
                  >
                    <i className="ti ti-filter-check fs-15 text-muted" />
                    <span>Status</span>
                    <span
                      className="text-capitalize ms-auto fw-semibold status-value-text"
                      style={{ fontSize: "13px" }}
                    >

                      {statusFilter === "all"
                        ? "All"
                        : statusFilter === "active"
                          ? "Active"
                          : "Inactive"}
                    </span>
                  </Link>

                  <div className="dropdown-menu dropdown-menu-start shadow-sm border-0 py-1">
                    <ul className="list-unstyled mb-0">
                      {[
                        {
                          label: "All",
                          value: "all",
                          icon: "ti ti-list-details",
                          count: dataSource.length,
                        },
                        {
                          label: "Active",
                          value: "active",
                          icon: "ti ti-check",
                          color: "text-success",
                          count: activeCount,
                        },
                        {
                          label: "Inactive",
                          value: "inactive",
                          icon: "ti ti-x",
                          color: "text-danger",
                          count: inactiveCount,
                        },
                      ].map(({ label, value, icon, color = "", count }) => {
                        const isActive = statusFilter === value;

                        return (
                          <li key={value}>
                            <Link
                              to="#"
                              className={`dropdown-item status-dropdown-item d-flex justify-content-between align-items-center gap-2 py-2 px-3 rounded ${isActive
                                ? "active fw-semibold"
                                : "text-dark hover-bg-light"
                                }`}
                              onClick={() => setStatusFilter(value)}
                              style={{ fontSize: "14px" }}
                            >

                              <span className="d-flex align-items-center gap-2">
                                <i
                                  className={`ti ${icon} fs-14 ${color} ${isActive ? "text-white" : ""
                                    }`}
                                />
                                {label}
                              </span>

                              <span className="d-flex align-items-center gap-2">
                                <span
                                  className={`badge rounded-pill px-2 py-1 fw-medium text-xs ${isActive
                                    ? "bg-white text-dark"
                                    : value === "all"
                                      ? "bg-secondary text-white"
                                      : value === "active"
                                        ? "bg-success text-white"
                                        : "bg-danger text-white"
                                    }`}
                                >

                                  {count}
                                </span>
                                {isActive && (
                                  <i className="ti ti-check fs-13" />
                                )}
                              </span>
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
              </div>
              <div className="d-flex align-items-center gap-2 flex-wrap ms-2">
                {selectedRows.length > 0 && (
                  <button
                    type="button"
                    className={`btn btn-outline-danger d-flex align-items-center gap-2 ${selectedRows.length === 0
                      ? "opacity-50 cursor-not-allowed"
                      : ""
                      }`}
                    data-bs-toggle="modal"
                    data-bs-target="#bulk_delete_modal"
                    disabled={selectedRows.length === 0}
                  >
                    <i className="ti ti-trash fs-16" />
                    <span className="d-none d-sm-inline">Delete Selected</span>
                    {selectedRows.length > 0 && (
                      <span className="badge bg-danger rounded-pill ms-2">
                        {selectedRows.length}
                      </span>
                    )}
                  </button>
                )}
                <div className="d-flex align-items-center ms-2 p-1 border bg-white rounded" style={{ height: "35px" }}>
                  <button
                    className={`btn btn-icon btn-sm view-mode-btn rounded ${viewMode === 'list' ? 'active' : 'text-muted'}`}
                    onClick={() => setViewMode('list')}
                    title="List View"
                    style={{ width: "28px", height: "28px", display: "flex", alignItems: "center", justifyContent: "center" }}
                  >
                    <i className="ti ti-list-details" />
                  </button>
                  <button
                    className={`btn btn-icon btn-sm view-mode-btn rounded ms-1 ${viewMode === 'grid' ? 'active' : 'text-muted'}`}
                    onClick={() => setViewMode('grid')}
                    title="Grid View"
                    style={{ width: "28px", height: "28px", display: "flex", alignItems: "center", justifyContent: "center" }}
                  >

                    <i className="ti ti-layout-grid" />
                  </button>
                </div>
                <div className="dropdown me-2 ms-2">
                  <Link
                    to="#"
                    className="btn bg-soft-purple text-purple"
                    data-bs-toggle="dropdown"
                    data-bs-auto-close="outside"
                  >
                    <i className="ti ti-columns-3 me-2" />
                    Manage Columns
                  </Link>
                  <div
                    className="dropdown-menu dropdown-menu-end dropdown-md p-3"
                    style={{ minWidth: "250px" }}
                  >
                    <h4 className="mb-2 fw-semibold">Manage Columns</h4>
                    <p className="mb-3">
                      Drag and drop columns to reorder and toggle visibility.
                    </p>
                    <div className="border-top pt-3">
                      {/* {Object.keys(visibleColumns).map((colKey) => (
                        <div
                          className="d-flex align-items-center justify-content-between mb-3"
                          key={colKey}
                        >
                          <p className="mb-0 d-flex align-items-center text-capitalize">
                            <i className="ti ti-grip-vertical me-2" />
                            {colKey.replace(/_/g, " ")}
                          </p>
                          <div className="status-toggle">
                            <input
                              type="checkbox"
                              className="check"
                              id={`col-${colKey}`}
                              checked={
                                visibleColumns[
                                  colKey as keyof typeof visibleColumns
                                ]
                              }
                              onChange={() => handleColumnToggle(colKey)}
                            />
                            <label
                              htmlFor={`col-${colKey}`}
                              className="checktoggle"
                            />
                          </div>
                        </div>
                      ))} */}
                      <ColumnManagement
                        columnKeys={columnKeys}
                        visibleColumns={visibleColumns}
                        onColumnOrderChange={setColumnKeys}
                        onColumnToggle={handleColumnToggle}
                        fixedKeys={["action"]}
                      />

                    </div>
                  </div>
                </div>

                <div className="form-sorts dropdown">
                  <Link
                    to="#"
                    className="dropdown-toggle dropdown-active-toggle common-filter-btn d-flex align-items-center gap-2 fw-medium"
                    data-bs-toggle="dropdown"
                    data-bs-auto-close="outside"
                  >

                    <i className="ti ti-filter-share" /> Filter
                    {(filterNames.length > 0 ||
                      filterStatuses.length > 0 ||
                      filterCreatedAts.length > 0) && (
                        <span className="badge filter-btn-badge rounded-pill ms-2">
                          {filterNames.length +
                            filterStatuses.length +
                            filterCreatedAts.length}
                        </span>
                      )}
                  </Link>

                  <div className="filter-dropdown-menu dropdown-menu dropdown-menu-md-end p-3">
                    <div className="filter-set-view">
                      <div className="filter-set-head">
                        <h4>
                          <i className="ti ti-filter-share" /> Filter
                        </h4>
                      </div>

                      <div className="accordion" id="accordionExample">
                        {/* Product Name Filter */}
                        <div className="filter-set-content">
                          <div className="filter-set-content-head d-flex align-items-center gap-2">
                            <Link
                              to="#"
                              className={`filter-accordion-link ${
                                openAccordion === "productName"
                                  ? ""
                                  : "collapsed"
                              }`}
                              onClick={() =>
                                handleAccordionToggle("productName")
                              }
                            >
                              Product Name
                            </Link>
                            {filterNames.length > 0 && (
                              <span className="badge filter-btn-badge rounded-pill small">
                                {filterNames.length}
                              </span>
                            )}
                          </div>
                          {openAccordion === "productName" && (
                            <div className="filter-set-contents accordion-collapse collapse show">
                              <div className="filter-content-list">
                                <div className="mb-2 icon-form">
                                  <span className="form-icon">
                                    <i className="ti ti-search" />
                                  </span>
                                  <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search Product Name"
                                    value={filterNamesSearch}
                                    onChange={(e) =>
                                      setFilterNamesSearch(e.target.value)
                                    }
                                  />
                                </div>

                                <ul className="list-unstyled mb-0">
                                  {uniqueNames
                                    .filter((n) =>
                                      n
                                        .toLowerCase()
                                        .includes(
                                          filterNamesSearch.toLowerCase()
                                        )
                                    )
                                    .map((name) => (
                                      <li key={name}>
                                        <div className="filter-checks">
                                          <label className="checkboxs">
                                            <input
                                              type="checkbox"
                                              checked={filterNames.includes(
                                                name
                                              )}
                                              onChange={() =>
                                                handleFilterChange(
                                                  name,
                                                  filterNames,
                                                  setFilterNames
                                                )
                                              }
                                            />
                                            <span className="checkmarks" />{" "}
                                            {name}
                                          </label>
                                        </div>
                                      </li>
                                    ))}

                                  {uniqueNames.filter((n) =>
                                    n
                                      .toLowerCase()
                                      .includes(filterNamesSearch.toLowerCase())
                                  ).length === 0 && (
                                      <p className="text-muted px-2">
                                        No data found
                                      </p>
                                    )}
                                </ul>
                              </div>
                            </div>
                          )}
                        </div>

                        <hr className="my-2" />

                        {/* Status Filter */}
                        <div className="filter-set-content">
                          <div className="filter-set-content-head d-flex align-items-center gap-2">
                            <Link
                              to="#"
                              className={
                                openAccordion === "status" ? "" : "collapsed"
                              }
                              onClick={() => handleAccordionToggle("status")}
                            >
                              Status
                            </Link>
                            {filterStatuses.length > 0 && (
                              <span className="badge bg-primary rounded-pill small">
                                {filterStatuses.length}
                              </span>
                            )}
                          </div>
                          {openAccordion === "status" && (
                            <div className="filter-set-contents accordion-collapse collapse show">
                              <div className="filter-content-list">
                                <div className="mb-2 icon-form">
                                  <span className="form-icon">
                                    <i className="ti ti-search" />
                                  </span>
                                  <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search Status"
                                    value={filterStatusesSearch}
                                    onChange={(e) =>
                                      setFilterStatusesSearch(e.target.value)
                                    }
                                  />
                                </div>

                                <ul className="list-unstyled mb-0">
                                  {uniqueStatuses
                                    .filter((s) =>
                                      s
                                        .toLowerCase()
                                        .includes(
                                          filterStatusesSearch.toLowerCase()
                                        )
                                    )
                                    .map((status) => (
                                      <li key={status}>
                                        <div className="filter-checks">
                                          <label className="checkboxs">
                                            <input
                                              type="checkbox"
                                              checked={filterStatuses.includes(
                                                status
                                              )}
                                              onChange={() =>
                                                handleFilterChange(
                                                  status,
                                                  filterStatuses,
                                                  setFilterStatuses
                                                )
                                              }
                                            />
                                            <span className="checkmarks" />{" "}
                                            {status}
                                          </label>
                                        </div>
                                      </li>
                                    ))}

                                  {uniqueStatuses.filter((s) =>
                                    s
                                      .toLowerCase()
                                      .includes(
                                        filterStatusesSearch.toLowerCase()
                                      )
                                  ).length === 0 && (
                                      <p className="text-muted px-2">
                                        No data found
                                      </p>
                                    )}
                                </ul>
                              </div>
                            </div>
                          )}
                        </div>

                        <hr className="my-2" />

                        {/* Created At Filter */}
                        <div className="filter-set-content">
                          <div className="filter-set-content-head d-flex align-items-center gap-2">
                            <Link
                              to="#"
                              className={
                                openAccordion === "createdAt" ? "" : "collapsed"
                              }
                              onClick={() => handleAccordionToggle("createdAt")}
                            >
                              Created At
                            </Link>
                            {filterCreatedAts.length > 0 && (
                              <span className="badge bg-primary rounded-pill small">
                                {filterCreatedAts.length}
                              </span>
                            )}
                          </div>
                          {openAccordion === "createdAt" && (
                            <div className="filter-set-contents accordion-collapse collapse show">
                              <div className="filter-content-list">
                                <div className="mb-2 icon-form">
                                  <span className="form-icon">
                                    <i className="ti ti-search" />
                                  </span>
                                  <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search Created At"
                                    value={filterCreatedAtsSearch}
                                    onChange={(e) =>
                                      setFilterCreatedAtsSearch(e.target.value)
                                    }
                                  />
                                </div>

                                <ul className="list-unstyled mb-0">
                                  {uniqueCreatedAts
                                    .filter((d) =>
                                      d
                                        .toLowerCase()
                                        .includes(
                                          filterCreatedAtsSearch.toLowerCase()
                                        )
                                    )
                                    .map((date) => (
                                      <li key={date}>
                                        <div className="filter-checks">
                                          <label className="checkboxs">
                                            <input
                                              type="checkbox"
                                              checked={filterCreatedAts.includes(
                                                date
                                              )}
                                              onChange={() =>
                                                handleFilterChange(
                                                  date,
                                                  filterCreatedAts,
                                                  setFilterCreatedAts
                                                )
                                              }
                                            />
                                            <span className="checkmarks" />{" "}
                                            {date}
                                          </label>
                                        </div>
                                      </li>
                                    ))}

                                  {uniqueCreatedAts.filter((d) =>
                                    d
                                      .toLowerCase()
                                      .includes(
                                        filterCreatedAtsSearch.toLowerCase()
                                      )
                                  ).length === 0 && (
                                      <p className="text-muted px-2">
                                        No data found
                                      </p>
                                    )}
                                </ul>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Reset / Apply Buttons */}
                      <div className="filter-reset-btns mt-3">
                        <div className="row">
                          <div className="col-6">
                            <Link
                              to="#"
                              className="btn btn-light w-100"
                              onClick={() => {
                                setFilterNames([]);
                                setFilterStatuses([]);
                                setFilterCreatedAts([]);
                                setFilterNamesSearch("");
                                setFilterStatusesSearch("");
                                setFilterCreatedAtsSearch("");
                                setOpenAccordion(null);
                              }}
                            >
                              Reset
                            </Link>
                          </div>
                          <div className="col-6">
                            <Link
                              to="#"
                              className="btn btn-primary w-100"
                              data-bs-dismiss="dropdown"
                            >
                              Apply
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 🔹 ACTIVE FILTER TITLES – MUST BE ABOVE TABLE */}
            {(filterNames.length > 0 ||
              filterStatuses.length > 0 ||
              filterCreatedAts.length > 0 ||
              statusFilter !== "all" ||
              alphaFilter !== "All" ||
              selectedDateRange ||
              searchTerm.length > 0 || // ✅ ADD
              sortOption !== "recently_added") && ( // ✅ ADD
                <div className="active-filters d-flex flex-wrap gap-2 mb-4 align-items-center-3">
                  {/* Search */}
                  {searchTerm.length > 0 && (
                    <div className="alert alert-primary py-2 mb-0">
                      <strong>Search:</strong> {searchTerm}
                      <i
                        className="ti ti-x ms-2 cursor-pointer"
                        onClick={() => setSearchTerm("")}
                      />
                    </div>
                  )}

                  {/* Sort */}
                  {sortOption !== "recently_added" && (
                    <div className="alert alert-secondary py-2 mb-0">
                      <strong>Sort:</strong>{" "}
                      {sortOption
                        .replace(/_/g, " ")
                        .replace(/\b\w/g, (c) => c.toUpperCase())}
                      <i
                        className="ti ti-x ms-2 cursor-pointer"
                        onClick={() => setSortOption("recently_added")}
                      />
                    </div>
                  )}

                  {filterNames.length > 0 && (
                    <div className="alert alert-primary py-2 mb-0">
                      <strong>Product type:</strong> {filterNames.join(", ")}
                      <i
                        className="ti ti-x ms-2 cursor-pointer"
                        onClick={() => setFilterNames([])}
                      />
                    </div>
                  )}

                  {filterStatuses.length > 0 && (
                    <div className="alert alert-warning py-2 mb-0">
                      <strong>Status:</strong> {filterStatuses.join(", ")}
                      <i
                        className="ti ti-x ms-2 cursor-pointer"
                        onClick={() => setFilterStatuses([])}
                      />
                    </div>
                  )}

                  {statusFilter !== "all" && (
                    <div className="alert alert-info py-2 mb-0">
                      <strong>Status:</strong>{" "}
                      {statusFilter.charAt(0).toUpperCase() +
                        statusFilter.slice(1)}
                      <i
                        className="ti ti-x ms-2 cursor-pointer"
                        onClick={() => setStatusFilter("all")}
                      />
                    </div>
                  )}

                  {filterCreatedAts.length > 0 && (
                    <div className="alert alert-secondary py-2 mb-0">
                      <strong>Created At:</strong> {filterCreatedAts.join(", ")}
                      <i
                        className="ti ti-x ms-2 cursor-pointer"
                        onClick={() => setFilterCreatedAts([])}
                      />
                    </div>
                  )}

                  {alphaFilter !== "All" && (
                    <div className="alert alert-success py-2 mb-0">
                      <strong>Title starts with:</strong> {alphaFilter}
                      <i
                        className="ti ti-x ms-2 cursor-pointer"
                        onClick={() => setAlphaFilter("All")}
                      />
                    </div>
                  )}

                  {selectedDateRange && (
                    <div className="alert alert-dark py-2 mb-0">
                      <strong>Date:</strong>{" "}
                      {selectedDateRange[0].toLocaleDateString()} –{" "}
                      {selectedDateRange[1].toLocaleDateString()}
                      <i
                        className="ti ti-x ms-2 cursor-pointer"
                        onClick={() => setSelectedDateRange(null)}
                      />
                    </div>
                  )}
                </div>
              )}

            
              <div className="table-responsive custom-table">
                {viewMode === 'grid' ? (
                  <div className="p-3">
                    <style>
                      {`
                            .hover-actions {
                              opacity: 0;
                              transition: opacity 0.2s ease-in-out;
                            }
                            .grid-card:hover .hover-actions {
                              opacity: 1;
                            }
                            .grid-card:hover {
                              transform: translateY(-5px);
                              box-shadow: 0 10px 20px rgba(0,0,0,0.08) !important;
                            }
                            .grid-avatar {
                              background: linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%);
                              color: white;
                              box-shadow: 0 4px 10px rgba(255, 107, 107, 0.3);
                            }
                          `}
                    </style>
                    <GridView
                      data={filteredData.slice((currentPage - 1) * pageSize, currentPage * pageSize)}
                      renderCard={(record: ProductType) => (
                        <div className="col-xxl-3 col-xl-4 col-md-6 mb-4" key={record.product_type_id}>
                          <div className="card h-100 border-0 shadow-sm transition-all grid-card position-relative overflow-hidden" style={{ borderRadius: '12px' }}>
                            <div className="card-body p-4 d-flex flex-column">
                              {/* Header */}
                              <div className="d-flex justify-content-between align-items-start mb-3">
                                <div className="avatar avatar-lg rounded-circle grid-avatar fw-bold d-flex align-items-center justify-content-center"
                                  style={{
                                    width: '50px',
                                    height: '50px',
                                    fontSize: '1.4rem',
                                    background: 'linear-gradient(135deg, #4A90E2 0%, #007AFF 100%)',
                                    color: 'white',
                                    boxShadow: '0 4px 10px rgba(74, 144, 226, 0.3)'
                                  }}>
                                  {record.product_name?.charAt(0).toUpperCase()}
                                </div>
                                <div className="d-flex gap-2 hover-actions">
                                  <button
                                    className="btn btn-sm btn-icon btn-light text-primary shadow-sm rounded-circle"
                                    onClick={() => handleEditClick(record)}
                                    data-bs-toggle="modal"
                                    data-bs-target="#edit_modal"
                                    title="Edit"
                                    style={{ width: '32px', height: '32px' }}
                                  >
                                    <i className="ti ti-edit fs-14" />
                                  </button>
                                  <button
                                    className="btn btn-sm btn-icon btn-light text-danger shadow-sm rounded-circle"
                                    onClick={() => setDeleteProductId(record.product_type_id!)}
                                    data-bs-toggle="modal"
                                    data-bs-target="#delete_modal"
                                    title="Delete"
                                    style={{ width: '32px', height: '32px' }}
                                  >
                                    <i className="ti ti-trash fs-14" />
                                  </button>
                                </div>
                              </div>

                              {/* Content */}
                              <h5 className="fw-bold text-dark mb-1 text-truncate" title={record.product_name}>
                                {highlightMatch(record.product_name, searchTerm)}
                              </h5>
                              <p className="text-muted small mb-3">
                                Product Type
                              </p>

                              <div className="d-flex align-items-center justify-content-between mb-3">
                                <span className={`badge rounded-pill fw-medium ${record.status === 'Active' ? 'bg-success-light text-success' : 'bg-danger-light text-danger'}`}>
                                  <i className={`ti ti-${record.status === 'Active' ? 'circle-check' : 'circle-x'} me-1`} />
                                  {record.status}
                                </span>
                              </div>

                              {/* Footer */}
                              <div className="mt-auto pt-3 border-top d-flex align-items-center justify-content-between text-muted small">
                                <div className="d-flex align-items-center">
                                  <i className="ti ti-calendar me-1" />
                                  Created:
                                </div>
                                <div className="fw-medium">
                                  {record.created_at ? new Date(record.created_at).toLocaleDateString("en-IN") : "—"}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    />
                    {/* Grid View Pagination */}
                    <div className="d-flex justify-content-end mt-4">
                      <Pagination
                        current={currentPage}
                        pageSize={pageSize}
                        total={filteredData.length}
                        onChange={(page, size) => {
                          setCurrentPage(page);
                          setPageSize(size);
                        }}
                        showSizeChanger
                        pageSizeOptions={["10", "20", "50", "75", "100"]}
                      />
                    </div>
                  </div>
                ) : (
                  <Table
                    columns={columns
                      .sort(
                        (a, b) =>
                          columnKeys.indexOf(a.key as string) -
                          columnKeys.indexOf(b.key as string)
                      )
                      .filter(
                        (col) =>
                          visibleColumns[col.key as keyof typeof visibleColumns]
                      )}
                    dataSource={filteredData}
                    enableRowSelection={true}
                    loading={loading}
                    rowKey="product_type_id"
                    className="custom-table"
                    emptyText="No Product Type found"
                    emptyIcon="ti ti-package-off"
                    recordLabel="Product Type"
                    rowSelection={{
                      selectedRowKeys: selectedRows,
                      onChange: (selectedRowKeys: React.Key[]) => {
                        setSelectedRows(selectedRowKeys as string[]);
                      },
                    }}
                    pagination={{
                      current: currentPage,
                      pageSize: pageSize,
                      showSizeChanger: true,
                      pageSizeOptions: ["10", "20", "50", "75", "100"],
                      onChange: (page: number, size: number) => {
                        setCurrentPage(page);
                        setPageSize(size);
                      },
                    }}
                    draggable={true}
                    enableColumnDrag={true}
                    onColumnOrderChange={(newCols: any[]) => {
                      // Get the new order of non-sticky columns from the dragged columns
                      const newOrderedKeys = newCols
                        .filter((c) => !c.fixed) // Only non-sticky columns
                        .map((c) => c.key || c.dataIndex)
                        .filter(
                          (key) =>
                            key !== undefined && key !== null && key !== ""
                        ); // Remove invalid keys

                      // Preserve columns that are not in newCols (hidden columns) by merging with existing columnKeys
                      const existingKeys = columnKeys;
                      const allNonStickyKeys = defaultColumnOrder; // All possible non-sticky column keys

                      // Create a new array that:
                      // 1. Uses the new order for visible columns
                      // 2. Preserves the order of hidden columns that aren't in newCols
                      const preservedKeys = existingKeys.filter(
                        (key) => !newOrderedKeys.includes(key)
                      );
                      const finalKeys = [...newOrderedKeys, ...preservedKeys];

                      // Ensure all default columns are included (in case some are missing)
                      const missingKeys = allNonStickyKeys.filter(
                        (key) => !finalKeys.includes(key)
                      );
                      const completeKeys = [...finalKeys, ...missingKeys];

                      setColumnKeys(completeKeys);
                      // Don't auto-save, let user decide on navigation/refresh
                    }}
                    onRowOrderChange={handleRowOrderChange}
                    scroll={{ x: "max-content" }}
                    components={{
                      header: {
                        cell: (props: any) => (
                          <th
                            {...props}
                            style={{
                              ...props.style,
                              position: "sticky",
                              top: 0,
                              zIndex: 2, // Better layering over scrollbars/fixed columns
                              background: "#fff",
                              boxShadow: "inset 0 -1px 0 #CBD5E1",
                            }}
                          />
                        ),
                      },
                    }}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Add Modal */}
          {/* Add New Product Type Modal - Premium Enhanced */}
          <div
            className="modal fade effect-modal-show"
            id="add_modal"
            tabIndex={-1}
            role="dialog"
            aria-labelledby="addProductTypeLabel"
            aria-hidden="true"
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5
                    className="modal-title text-primary fw-bold"
                    id="addProductTypeLabel"
                  >
                    <i className="ti ti-square-rounded-plus-filled me-2 fs-20" />
                    Add New Product Type
                  </h5>
                  <button
                    type="button"
                    className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                  >
                    <i className="ti ti-x" />
                  </button>
                </div>

                <form onSubmit={(e) => e.preventDefault()}>
                  <div className="modal-body">
                    {/* Product Type Name */}
                    <div className="mb-3">
                      <label
                        className={`col-form-label fw-semibold ${addNameError ? "pro-shake text-danger" : "text-dark"
                          }`}
                        htmlFor="add-product-type-input"
                      >
                        Product Type Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        id="add-product-type-input"
                        className={`form-control form-control-lg ${addNameError ? "is-invalid border-danger" : ""
                          }`}
                        value={newName}
                        onChange={(e) => {
                          const value = e.target.value;
                          setNewName(value);

                          let res: ValidationResult =
                            validateProducttype(value);
                          if (!res.valid) {
                          } else res = validateNoDoubleSpacesLead(value);
                          if (!res.valid) {
                          } else res = validateNotOnlySymbolsLead(value);
                          if (!res.valid) {
                          } else res = validateNoRepeatedCharactersLead(value);
                          if (!res.valid) {
                          } else
                            res = validateNoEdgeSymbols(
                              value,
                              "Product Type Name"
                            );
                          if (!res.valid) {
                          } else res = validateNotOnlyNumbersLead(value);
                          if (!res.valid) {
                          } else
                            res = validateProductTypeDuplicate(
                              value,
                              dataSource.map((d) => d.product_name || "")
                            );

                          const hasError = !res.valid;
                          setAddNameError(res.valid ? "" : res.message);
                          setIsCreateValid(
                            res.valid && validateLeadStatus(newStatus).valid
                          );

                          // PRO SHAKE
                          const label = document.querySelector(
                            '#add_modal label[for="add-product-type-input"]'
                          ) as HTMLElement;
                          if (label) {
                            label.classList.remove("pro-shake");
                            if (hasError) {
                              void label.offsetHeight;
                              label.classList.add("pro-shake");
                            }
                          }
                        }}
                        placeholder="e.g. Software License, Hardware Device, Service Contract"
                        autoFocus
                      />
                      {addNameError && (
                        <small className="text-danger fw-medium">
                          {addNameError}
                        </small>
                      )}
                    </div>

                    {/* Status */}
                    <div className="mb-0">
                      <label className="col-form-label d-block fw-semibold">
                        Status
                      </label>
                      <div className="d-flex align-items-center gap-3">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="radio"
                            id="add-active"
                            name="status_add"
                            checked={newStatus === "Active"}
                            onChange={() => setNewStatus("Active")}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="add-active"
                          >
                            Active
                          </label>
                        </div>
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="radio"
                            id="add-inactive"
                            name="status_add"
                            checked={newStatus === "Inactive"}
                            onChange={() => setNewStatus("Inactive")}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="add-inactive"
                          >
                            Inactive
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="modal-footer">
                    <div className="d-flex align-items-center justify-content-end m-0 w-100">
                      <button
                        type="button"
                        className="btn btn-light me-2"
                        data-bs-dismiss="modal"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary"
                        disabled={!isCreateValid || saving}
                        onClick={handleCreateProduct}
                        data-bs-dismiss="modal"
                      >
                        {saving ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                            />
                            Creating...
                          </>
                        ) : (
                          <>
                            <i className="ti ti-check me-2" />
                            Create Product Type
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>

          {/* Edit Product Type Modal - Premium Enhanced */}
          <div
            className="modal fade effect-modal-show"
            id="edit_modal"
            tabIndex={-1}
            role="dialog"
            aria-labelledby="editProductTypeLabel"
            aria-hidden="true"
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5
                    className="modal-title text-primary fw-bold"
                    id="editProductTypeLabel"
                  >
                    <i className="ti ti-edit me-2 fs-20" />
                    Edit Product Type
                  </h5>
                  <button
                    type="button"
                    className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                  >
                    <i className="ti ti-x" />
                  </button>
                </div>

                <form onSubmit={(e) => e.preventDefault()}>
                  <div className="modal-body">
                    {/* Product Type Name */}
                    <div className="mb-3">
                      <label
                        className={`col-form-label fw-semibold ${editNameError ? "pro-shake text-danger" : "text-dark"
                          }`}
                        htmlFor="edit-product-type-input"
                      >
                        Product Type Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        id="edit-product-type-input"
                        className={`form-control form-control-lg ${editNameError ? "is-invalid border-danger" : ""
                          }`}
                        value={editName}
                        onChange={(e) => {
                          const value = e.target.value;
                          setEditName(value);

                          let res: ValidationResult =
                            validateProducttype(value);
                          if (!res.valid) {
                          } else res = validateNoDoubleSpacesLead(value);
                          if (!res.valid) {
                          } else res = validateNotOnlySymbolsLead(value);
                          if (!res.valid) {
                          } else res = validateNoRepeatedCharactersLead(value);
                          if (!res.valid) {
                          } else
                            res = validateNoEdgeSymbols(
                              value,
                              "Product Type Name"
                            );
                          if (!res.valid) {
                          } else res = validateNotOnlyNumbersLead(value);
                          if (!res.valid) {
                          } else
                            res = validateProductTypeDuplicate(
                              value,
                              dataSource
                                .map((d) => d.product_name || "")
                                .filter((n) => n !== editProduct?.product_name)
                            );

                          const hasError = !res.valid;
                          setEditNameError(res.valid ? "" : res.message);
                          setIsEditValid(
                            res.valid && validateLeadStatus(editStatus).valid
                          );

                          // PRO SHAKE
                          const label = document.querySelector(
                            '#edit_modal label[for="edit-product-type-input"]'
                          ) as HTMLElement;
                          if (label) {
                            label.classList.remove("pro-shake");
                            if (hasError) {
                              void label.offsetHeight;
                              label.classList.add("pro-shake");
                            }
                          }
                        }}
                        placeholder="Edit product type name"
                        autoFocus
                      />
                      {editNameError && (
                        <small className="text-danger fw-medium">
                          {editNameError}
                        </small>
                      )}
                    </div>

                    {/* Status */}
                    <div className="mb-0">
                      <label className="col-form-label d-block fw-semibold">
                        Status
                      </label>
                      <div className="d-flex align-items-center gap-3">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="radio"
                            id="edit-active"
                            name="status_edit"
                            checked={editStatus === "Active"}
                            onChange={() => setEditStatus("Active")}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="edit-active"
                          >
                            Active
                          </label>
                        </div>
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="radio"
                            id="edit-inactive"
                            name="status_edit"
                            checked={editStatus === "Inactive"}
                            onChange={() => setEditStatus("Inactive")}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="edit-inactive"
                          >
                            Inactive
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="modal-footer">
                    <div className="d-flex align-items-center justify-content-end m-0 w-100">
                      <button
                        type="button"
                        className="btn btn-light me-2"
                        data-bs-dismiss="modal"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary"
                        disabled={!isEditValid || saving}
                        onClick={handleSaveEdit}
                        data-bs-dismiss="modal"
                      >
                        {saving ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                            />
                            Saving...
                          </>
                        ) : (
                          <>
                            <i className="ti ti-check me-2" />
                            Save Changes
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>

          <ModalDeleteEffect />

          {/* Single Delete Modal - Enhanced (Consistent Style) */}
          <div
            className="modal fade effect-modal-show"
            id="delete_modal"
            tabIndex={-1}
            role="dialog"
            aria-labelledby="deleteProductTypeLabel"
            aria-hidden="true"
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5
                    className="modal-title text-danger fw-bold"
                    id="deleteProductTypeLabel"
                  >
                    <i className="ti ti-alert-triangle me-2" />
                    Confirm Deletion
                  </h5>
                  <button
                    type="button"
                    className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                  >
                    <i className="ti ti-x" />
                  </button>
                </div>
                <div className="modal-body text-center">
                  <div className="avatar avatar-xl bg-danger-light rounded-circle mb-4 mx-auto effect-modal-pulse">
                    <i className="ti ti-trash fs-40 text-danger" />
                  </div>
                  <h4 className="fw-bold text-dark mb-3">
                    Delete This Product Type?
                  </h4>
                  <p className="text-muted fs-15 mb-4 px-4">
                    This action <strong>cannot be undone</strong>. The product
                    type will be
                    <strong> permanently removed</strong>.
                  </p>
                  <div className="alert alert-warning py-3 mx-4 mb-4 rounded-3 border-warning">
                    <i className="ti ti-info-circle me-2" />
                    <strong>Warning:</strong> This may affect existing products
                    using this type.
                  </div>
                  <div className="d-flex gap-3 justify-content-center mt-4">
                    <button
                      type="button"
                      className="btn btn-light px-5 py-2 fw-medium"
                      data-bs-dismiss="modal"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger px-5 py-2 fw-medium"
                      data-bs-dismiss="modal"
                      onClick={handleConfirmDelete}
                      disabled={saving}
                    >
                      {saving ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                          />
                          Deleting...
                        </>
                      ) : (
                        <>
                          <i className="ti ti-trash me-2" />
                          Yes, Delete It
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bulk Delete Modal */}
      <div
        className="modal fade effect-modal-show"
        id="bulk_delete_modal"
        tabIndex={-1}
        role="dialog"
        aria-labelledby="bulkDeleteLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5
                className="modal-title text-danger fw-bold"
                id="bulkDeleteLabel"
              >
                <i className="ti ti-alert-triangle me-2" />
                Confirm Bulk Deletion
              </h5>
              <button
                type="button"
                className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <div className="modal-body text-center">
              <div className="avatar avatar-xl bg-danger-light rounded-circle mb-4 mx-auto effect-modal-pulse">
                <i className="ti ti-trash fs-40 text-danger" />
              </div>
              <h4 className="fw-bold text-dark mb-3">
                Delete {selectedRows.length} Product Type
                {selectedRows.length > 1 ? "s" : ""}?
              </h4>
              <p className="text-muted fs-15 mb-4 px-4">
                This action <strong>cannot be undone</strong>. The selected
                product types will be
                <strong> permanently removed</strong> from your system.
              </p>
              <div className="alert alert-warning py-3 mx-4 mb-4 rounded-3 border-warning">
                <i className="ti ti-info-circle me-2" />
                <strong>Warning:</strong> This may affect products currently
                using these types.
              </div>
              <div className="d-flex gap-3 justify-content-center mt-4">
                <button
                  type="button"
                  className="btn btn-light px-5 py-2 fw-medium"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger px-5 py-2 fw-medium"
                  onClick={handleBulkDelete}
                  data-bs-dismiss="modal"
                  disabled={deleting}
                >
                  {deleting ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                      />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <i className="ti ti-trash me-2" />
                      Yes, Delete Permanently
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Import Excel Modal */}
      <div
        className="modal fade effect-modal-show"
        id="import_excel_modal"
        tabIndex={-1}
        role="dialog"
        aria-labelledby="importExcelLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5
                className="modal-title text-success fw-bold d-flex align-items-center gap-2"
                id="importExcelLabel"
              >
                <i className="ti ti-file-import fs-22" />
                Import Product Types
              </h5>
              <button
                type="button"
                className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>

            <div className="modal-body">
              {!importResult ? (
                <>
                  <div
                    className={`border-2 rounded-3 p-4 text-center transition-all ${importFile
                      ? "border-success bg-success-light"
                      : "border-dashed border-success bg-success-subtle"
                      }`}
                    style={{
                      cursor: "pointer",
                      minHeight: "180px",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.add(
                        "bg-success-light",
                        "border-success"
                      );
                    }}
                    onDragLeave={(e) => {
                      e.currentTarget.classList.remove(
                        "bg-success-light",
                        "border-success"
                      );
                      if (!importFile)
                        e.currentTarget.classList.add("border-dashed");
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.remove(
                        "bg-success-light",
                        "border-success"
                      );
                      const file = e.dataTransfer.files[0];
                      if (file && file.name.toLowerCase().endsWith(".xlsx")) {
                        setImportFile(file);
                      } else {
                        toast.error("Please upload a valid .xlsx file");
                      }
                    }}
                  >
                    <input
                      type="file"
                      accept=".xlsx"
                      className="d-none"
                      id="excel-upload"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) setImportFile(file);
                      }}
                    />

                    <i
                      className={`ti ti-file-upload fs-40 mb-3 ${importFile ? "text-success" : "text-success"
                        }`}
                    />

                    {importFile ? (
                      <div className="text-center">
                        <p className="fw-bold text-dark mb-1">
                          {importFile.name}
                        </p>
                        <p className="text-muted small mb-3">
                          {importFile.size > 1024 * 1024
                            ? (importFile.size / (1024 * 1024)).toFixed(1) +
                            " MB"
                            : (importFile.size / 1024).toFixed(1) + " KB"}
                        </p>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger rounded-pill px-3"
                          onClick={(e) => {
                            e.stopPropagation();
                            setImportFile(null);
                          }}
                        >
                          <i className="ti ti-x fs-14 me-1" />
                          Remove
                        </button>
                      </div>
                    ) : (
                      <>
                        <p className="fw-semibold text-dark mb-2">
                          Drag & drop Excel file here
                        </p>
                        <p className="text-muted small mb-2">or</p>
                        <label
                          htmlFor="excel-upload"
                          className="btn btn-success rounded-pill px-4 py-2 shadow-sm"
                        >
                          <i className="ti ti-file-upload me-2" />
                          Browse File
                        </label>
                        <p className="text-muted small mt-3 mb-0">
                          Supports <code>.xlsx</code> format only
                        </p>
                      </>
                    )}
                  </div>

                  <div className="text-center mt-3">
                    <a
                      href="/product_type.xlsx"
                      download="product_type_template.xlsx"
                      className="text-primary small d-inline-flex align-items-center fw-medium hover-text-primary-dark transition-colors"
                    >
                      <i className="ti ti-file-download me-1" />
                      Download Sample Template (.xlsx)
                    </a>
                  </div>
                </>
              ) : (
                <div className="text-center">
                  <div
                    className="mx-auto mb-4 d-inline-flex align-items-center justify-content-center rounded-circle bg-success text-white shadow-lg"
                    style={{ width: "90px", height: "90px" }}
                  >
                    <i className="ti ti-check fs-48" />
                  </div>
                  <h5 className="fw-bold text-success mb-3">
                    Import Successful!
                  </h5>
                  <p className="text-dark fw-semibold mb-2">
                    <strong>{importResult.added.length}</strong> product type
                    {importResult.added.length !== 1 ? "s" : ""} added
                  </p>
                  {importResult.skipped.length > 0 && (
                    <p className="text-danger fw-medium mb-0 d-flex align-items-center justify-content-center gap-2">
                      <i className="ti ti-alert-triangle fs-16" />
                      <strong>{importResult.skipped.length}</strong> row
                      {importResult.skipped.length !== 1 ? "s" : ""} skipped due
                      to errors
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="modal-footer">
              <div className="d-flex justify-content-end gap-3 w-100">
                {!importResult ? (
                  <>
                    <button
                      type="button"
                      className="btn btn-light px-4 py-2 rounded-3"
                      data-bs-dismiss="modal"
                      onClick={() => setImportFile(null)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-success px-5 py-2 rounded-3 d-flex align-items-center gap-2 shadow-sm"
                      disabled={!importFile || importing}
                      onClick={async () => {
                        if (!importFile) return;
                        setImporting(true);
                        try {
                          const result = await importProductTypeExcel(
                            importFile
                          );
                          setImportResult(result);
                          await loadProducts(); // your refresh function
                        } catch (err) {
                          // toast handled in API
                        } finally {
                          setImporting(false);
                        }
                      }}
                    >
                      {importing ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" />
                          Importing Product Type...
                        </>
                      ) : (
                        <>
                          <i className="ti ti-table-import" />
                          Import File
                        </>
                      )}
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="btn btn-primary px-5 py-2 rounded-3 w-100"
                    data-bs-dismiss="modal"
                    onClick={() => {
                      setImportResult(null);
                      setImportFile(null);
                    }}
                  >
                    Done
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Save Column Order Modal */}
      <SaveModal />
      <ScrollToTop />
    </>
  );
};

export default Producttype;
